---
layout: docs
title: "What Gets Deployed"
---


When you deploy Corral from Azure Marketplace, the following resources are provisioned in your tenant. Everything appears in your Azure portal under your subscription.

---

## Resource Inventory

| Resource | Azure Service | Purpose |
|---|---|---|
| Managed Identity | User Assigned Managed Identity | Core identity for all Corral services — authenticates between components |
| Monitoring | Log Analytics Workspace | Centralized logging for all application and infrastructure events |
| Telemetry | Application Insights | Performance monitoring, usage telemetry, distributed tracing |
| Secrets | Key Vault | Secrets, certificates, data protection keys |
| File Storage | Storage Account | Application state, user files (workspace and session), Functions runtime |
| Database | SQL Server + Database | All operational data — users, root projects, projects, apps, configurations, conversations |
| Compute | Container Apps Environment | Hosts all application containers in a shared environment |
| AI Models | AI Foundry Hub + Project | Model deployments for LLM inference |
| Static Assets | Static Web App | CDN for embedded widgets and static files |
| Code Execution | Session Pool | Sandboxed code execution for AI agents (Azure Container Apps Dynamic Sessions) |
| API Server | Container App | Core API — handles all client requests, agent orchestration, tool execution |
| Functions | Container App | Background jobs — publishing, analytics, marketplace event handling |
| Document Processing | Container App | File conversion and document analysis |
| Database Migrations | Container App | Schema migrations on deployment |

---

## What's NOT Deployed

- **Cosmos DB** — not used. SQL Server handles all operational data.
- **Private endpoints** — not configured by default. Resources use public endpoints with authentication and firewall rules. Private networking is available and can be enabled.
- **VNet integration** — not enabled by default. The Container Apps Environment supports it, and the infrastructure templates include the configuration.

See [Network & Data Residency →](./network-and-data-residency) for details on networking options.

---

## How Resources Connect

<!-- TODO: Architecture diagram showing how the Container Apps connect to SQL, Storage, Key Vault, AI Foundry, and Session Pool. Show the managed resource group boundary. This would be a great visual to include — similar to what's on the current marketing site but accurate to the actual resource topology. -->

*This section is a work in progress.*


---

## Cost Considerations

All resources run on your Azure subscription and are billed directly by Azure. Key cost drivers:

| Resource | Cost Factor |
|---|---|
| Container Apps | vCPU and memory consumption — scales with usage |
| SQL Database | DTU or vCore tier selected during deployment |
| AI Foundry | Token consumption for model inference |
| Storage Account | Data volume (user files, state, logs) |
| Session Pool | Session time for code execution |
| Static Web App | Minimal — CDN serving |

Corral's platform subscription is a separate, fixed charge on your Marketplace bill. The Azure resource consumption is variable and depends on your usage patterns.

<!-- TODO: Rough cost estimates for typical deployment sizes — small team (5-20 users), medium (50-200), large (500+). Even ballpark ranges would help buyers budget. -->

*This section is a work in progress.*


---

## Inspecting Your Deployment

Everything is visible in the Azure portal:

- **Resource Group** → see all provisioned resources
- **Activity Log** → see every operation, including Corral maintenance actions
- **Application Insights** → see application performance and usage
- **Log Analytics** → query logs across all services
- **Key Vault** → see (but not necessarily read) all secrets
- **SQL Database** → query your operational data directly if needed

You have the same visibility into Corral's deployment as you do into any other Azure resource you own.
